import React, { useState, useEffect } from 'react';
import { X, CheckCircle, Mail, Clock, Shield, Zap, TrendingUp, FileText } from 'lucide-react';

const RequestModal = ({ isOpen, onClose }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    location: '',
    documentType: '',
    volume: '',
    additionalInfo: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);
  const [shouldRender, setShouldRender] = useState(false);

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  // Handle animation states
  useEffect(() => {
    if (isOpen) {
      setShouldRender(true);
      // Trigger enter animation after mounting - need double RAF to ensure DOM is ready
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setIsAnimating(true);
        });
      });
    } else {
      // Trigger exit animation
      setIsAnimating(false);
      // Remove from DOM after animation completes
      const timeout = setTimeout(() => {
        setShouldRender(false);
        // Reset form when modal is fully closed
        setIsSubmitted(false);
        setFormData({
          name: '',
          email: '',
          company: '',
          location: '',
          documentType: '',
          volume: '',
          additionalInfo: ''
        });
      }, 300); // Match the animation duration

      return () => clearTimeout(timeout);
    }
  }, [isOpen]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      // Determine the API URL based on environment
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:3001';
      
      const response = await fetch(`${apiUrl}/api/forms/demo-request`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      const data = await response.json();

      if (response.ok) {
        setIsSubmitting(false);
        setIsSubmitted(true);
        
        // Close modal after 2 seconds
        setTimeout(() => {
          onClose();
        }, 2000);
      } else {
        console.error('Error submitting form:', data.error);
        setIsSubmitting(false);
        // You might want to show an error message to the user here
        alert(data.error || 'Failed to submit request. Please try again.');
      }
    } catch (error) {
      console.error('Network error:', error);
      setIsSubmitting(false);
      alert('Network error. Please check your connection and try again.');
    }
  };

  const handleClose = () => {
    onClose();
  };

  if (!shouldRender) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-screen items-center justify-center p-4">
        {/* Backdrop */}
        <div 
          className={`fixed inset-0 bg-black/50 backdrop-blur-sm transition-all duration-300 ease-out ${
            isAnimating ? 'opacity-100' : 'opacity-0'
          }`}
          onClick={handleClose}
        />
        
        {/* Modal */}
        <div className={`relative bg-white rounded-2xl shadow-2xl w-full max-w-6xl max-h-[90vh] overflow-y-auto scrollbar-hide transition-all duration-300 ease-out ${
          isAnimating 
            ? 'opacity-100 scale-100 translate-y-0' 
            : 'opacity-0 scale-95 translate-y-8'
        }`}>
          {/* Close Button */}
          <button
            onClick={handleClose}
            className="absolute top-4 right-4 z-10 p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full transition-all duration-200"
          >
            <X size={20} />
          </button>

          {isSubmitted ? (
            // Success State
            <div className="p-12 text-center">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-8 h-8 text-green-600" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-2">Request submitted!</h3>
              <p className="text-gray-600">
                Thank you for your interest. We'll review your project details and send you a tailored proposal or schedule a meeting within 48 hours.
              </p>
            </div>
          ) : (
            // Form State
            <div className="flex flex-row min-h-[700px]">
              {/* Left Side - Company Info */}
            <div className="bg-gradient-to-br from-slate-900 via-blue-900 to-indigo-900 p-12 relative overflow-hidden flex-1">
                {/* Background Pattern */}
                <div className="absolute inset-0 opacity-10">
                  <div className="absolute top-0 left-0 w-32 h-32 bg-blue-400 rounded-full blur-3xl"></div>
                  <div className="absolute bottom-0 right-0 w-24 h-24 bg-indigo-400 rounded-full blur-2xl"></div>
                  <div className="absolute top-1/2 left-1/3 w-16 h-16 bg-cyan-400 rounded-full blur-xl"></div>
                </div>

                <div className="h-full flex flex-col justify-between relative z-10">
                  {/* Header Section */}
                  <div>
                    <h2 className="text-4xl font-bold text-white mb-3 leading-tight">
                      Transform Your <span className="text-blue-400">Documents</span> Into Structured Data
                    </h2>
                    
                    <p className="text-blue-100 text-lg mb-8 leading-relaxed">
                      Trusted by researchers worldwide for research-grade document digitization
                    </p>

                    {/* Stats Section */}
                    <div className="grid grid-cols-3 gap-4 mb-8">
                      <div className="text-center">
                        <div className="flex items-center justify-center mb-1">
                          <FileText className="w-4 h-4 text-blue-400 mr-1" />
                          <span className="text-2xl font-bold text-white">100K+</span>
                        </div>
                        <span className="text-blue-200 text-xs">Pages Processed</span>
                      </div>
                      <div className="text-center">
                        <div className="flex items-center justify-center mb-1">
                          <Zap className="w-4 h-4 text-yellow-400 mr-1" />
                          <span className="text-2xl font-bold text-white">48h</span>
                        </div>
                        <span className="text-blue-200 text-xs">Get Proposal</span>
                      </div>
                      <div className="text-center">
                        <div className="flex items-center justify-center mb-1">
                          <TrendingUp className="w-4 h-4 text-green-400 mr-1" />
                          <span className="text-2xl font-bold text-white">99.9%</span>
                        </div>
                        <span className="text-blue-200 text-xs">Typical Accuracy Rate</span>
                      </div>
                    </div>
                    
                    {/* Feature Cards */}
                    <div className="space-y-3 mt-8">
                    <div className="flex items-center gap-4 p-3 bg-white/5 backdrop-blur-sm rounded-xl border border-white/10">
                      <div className="w-12 h-12 bg-gradient-to-r from-blue-500 to-cyan-500 rounded-lg flex items-center justify-center flex-shrink-0">
                        <Shield className="w-5 h-5 text-white" />
                      </div>
                      <div className="text-left">
                        <h3 className="font-semibold text-white mb-1">Research-grade typical accuracy</h3>
                        <p className="text-blue-200 text-sm">Human-in-the-loop validation for complex tables and documents</p>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-4 p-3 bg-white/5 backdrop-blur-sm rounded-xl border border-white/10">
                      <div className="w-12 h-12 bg-gradient-to-r from-green-500 to-emerald-500 rounded-lg flex items-center justify-center flex-shrink-0">
                        <Clock className="w-5 h-5 text-white" />
                      </div>
                      <div className="text-left">
                        <h3 className="font-semibold text-white mb-1">Quick response</h3>
                        <p className="text-blue-200 text-sm">Receive detailed proposals or meeting invites within 48 hours</p>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-4 p-3 bg-white/5 backdrop-blur-sm rounded-xl border border-white/10">
                      <div className="w-12 h-12 bg-gradient-to-r from-purple-500 to-pink-500 rounded-lg flex items-center justify-center flex-shrink-0">
                        <Mail className="w-5 h-5 text-white" />
                      </div>
                      <div className="text-left">
                        <h3 className="font-semibold text-white mb-1">Full service support</h3>
                        <p className="text-blue-200 text-sm">End-to-end digitization managed by our expert team</p>
                      </div>
                    </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Right Side - Form */}
              <div className="p-12 flex-1 overflow-y-auto scrollbar-hide">
                <div className="h-full flex flex-col justify-center">
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">Get your custom quote</h3>
                  <p className="text-gray-600 mb-8">Tell us about your project and we'll send you a tailored proposal or schedule a meeting within 48 hours.</p>
                  
                  <form onSubmit={handleSubmit} className="space-y-6">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2 text-left">Full Name*</label>
                        <input
                          type="text"
                          name="name"
                          value={formData.name}
                          onChange={handleInputChange}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                          placeholder="Your name"
                          required
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2 text-left">Email*</label>
                        <input
                          type="email"
                          name="email"
                          value={formData.email}
                          onChange={handleInputChange}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                          placeholder="you@company.com"
                          required
                        />
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2 text-left">Company</label>
                        <input
                          type="text"
                          name="company"
                          value={formData.company}
                          onChange={handleInputChange}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                          placeholder="Your company"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2 text-left">Location</label>
                        <input
                          type="text"
                          name="location"
                          value={formData.location}
                          onChange={handleInputChange}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                          placeholder="City, Country"
                        />
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2 text-left">Document Type</label>
                        <input
                          type="text"
                          name="documentType"
                          value={formData.documentType}
                          onChange={handleInputChange}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                          placeholder="PDFs, Images, etc."
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2 text-left">Volume</label>
                        <input
                          type="text"
                          name="volume"
                          value={formData.volume}
                          onChange={handleInputChange}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                          placeholder="Pages"
                        />
                      </div>
                    </div>
                    

                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2 text-left">Project Details*</label>
                      <textarea
                        name="additionalInfo"
                        value={formData.additionalInfo}
                        onChange={handleInputChange}
                        rows={4}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors resize-none"
                        placeholder="Tell us more about your project, specific requirements, output format preferences, etc."
                        required
                      />
                    </div>
                    
                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="w-full bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white py-3 px-6 rounded-lg font-medium transition-colors disabled:cursor-not-allowed flex items-center justify-center"
                    >
                      {isSubmitting ? (
                        <>
                          <div className="animate-spin w-5 h-5 border-2 border-white border-t-transparent rounded-full mr-2"></div>
                          Submitting...
                        </>
                      ) : (
                        'Get My Custom Quote'
                      )}
                    </button>
                  </form>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default RequestModal;

